import {
  H4,
  FormGroup,
  HTMLSelect,
  NumericInput,
  InputGroup,
  Dialog,
} from "@blueprintjs/core";
import {
  ReactNode,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Button } from "components/Button";
import {
  runEncoderCalibration,
  runHallSensorCalibration,
} from "connections/controller";
import { Colors } from "design/colors";
import { Device, useDevices } from "hooks/useDevices";
import {
  Strings,
  Env,
  encoderCalibrationStatusToString,
  hallSensorCalibrationStatusToString,
  hallSensorDirectionToString,
  encoderCalibrationFailed,
  hallSensorCalibrationFailed,
} from "utils/common";
import {
  EncoderCalibStatus,
  EncoderDirection,
  HallsensorCalibStatus,
  HallsensorDirection,
  IEncoderCalResultResponse,
  IHallSensorCalResultResponse,
  PacketId,
  SensorMode,
  fullDisplayUnits,
} from "utils/common-types";
import { isSensorlessEnabled } from "utils/feature-flags";
import {
  IEditableSensorsConfig,
  INonEditableSensorsConfig,
  SensorsConfig,
} from "utils/firestore";
import {
  INavigationProps,
  Edit,
  FORM_WIDTH,
  inputStyle,
  configContainerStyle,
  configModalStyle,
  screenTitleStyle,
  inputContStyle,
} from "./CStoreEditor";

const DEFAULT_OPEN_LOOP_ACCEL_MKRADPS2 =   10; // mech. rad/s^2
const MAX_OPEN_LOOP_ACCEL_MKRADPS2     = 2000; // mech. rad/s^2

/*
 * Relevant device commands:
 *
 * Command-response:
 *
 *   S Labs sends START_ENCODER_CAL to start calibration. When completed or failed, controller replies with ENCODER_CAL_RESULT.
 *
 * Asynchronous:
 *
 *   At any time, S Labs may send a GET_ENCODER_CAL_RESULT request to controller. Controller replies with GET_ENCODER_CAL_RESULT response.
 *
 * */
export const SensorsScreen = (
  props: INavigationProps & {
    storedSensorsConfig: SensorsConfig,
    editableSensorsConfig: Strings<IEditableSensorsConfig>;
    editSensorsConfig: Edit<IEditableSensorsConfig>;
    nonEditableSensorsConfig: INonEditableSensorsConfig; // Updated by setEncoderState & setHallSensorState
    setNonEditableSensorsConfig: (cfg: INonEditableSensorsConfig) => void;
    setWarningContents: (contents: ReactNode) => void;
  }
) => {
  const { connectedDevice } = useDevices();
  const [isRunning, _setIsRunning] = useState<boolean>(false);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const {
    setEnableNavigation,
    next,
    prev,
    storedSensorsConfig,
    editableSensorsConfig,
    editSensorsConfig,
    nonEditableSensorsConfig,
    setNonEditableSensorsConfig,
    setWarningContents,
  } = props;
  const selectedSensorMode = parseInt( editableSensorsConfig.sensorMode, 10 ) as SensorMode;
  const [showCalibrationResultMessage, setShowCalibrationResultMessage] = useState<boolean>(false);
  const [ encoderCalibStatus, setEncoderCalibStatus ] = useState<EncoderCalibStatus | string | undefined>(); // string is for result validation failures
  const [ hallSensorCalibStatus, setHallSensorCalibStatus ] = useState<HallsensorCalibStatus | string | undefined>(); // string is for result validation failures

  const isHallSensorSupported = isFinite( nonEditableSensorsConfig.hallsensorForwardOffset ) &&
                                isFinite( nonEditableSensorsConfig.hallsensorReverseOffset ) &&
                                nonEditableSensorsConfig.hallsensorDirection !== HallsensorDirection.UNKNOWN;

  const lastValidEncoderCprRef = useRef<string>("0");
  const lastValidAccelerationRampRef = useRef<string>("0");

  const handleSensorModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sensorMode = e.target.value;
    editSensorsConfig("sensorMode", sensorMode);

    // Ensure there is a valid encoder CPR before switching away from Encoder sensor mode
    if (
      selectedSensorMode !== SensorMode.ENCODER &&
      !encoderCprIsValid(editableSensorsConfig.encoderCpr)
    ) {
      // Revert to most recent valid encoderCpr
      editSensorsConfig("encoderCpr", lastValidEncoderCprRef.current);
    }

    // Ensure there is a valid open loop acceleration ramp before switching away from Open Loop sensor mode
    if (
      selectedSensorMode !== SensorMode.OPENLOOP &&
      !openLoopAccelerationIsValid(editableSensorsConfig.accelerationRamp_mkradps2)
    ) {
      // Revert to most recent valid acceleration ramp
      editSensorsConfig(
        "accelerationRamp_mkradps2",
        lastValidAccelerationRampRef.current
      );
    }
  };

  // Wraps editSensorsConfig so that the most recent valid encoderCpr is available to be reverted to later.
  const updateEncoderCpr = (newEncoderCpr: string) => {
    if (encoderCprIsValid(newEncoderCpr)) {
      lastValidEncoderCprRef.current = newEncoderCpr;
    }
    editSensorsConfig("encoderCpr", newEncoderCpr);
  };

  // Wraps editSensorsConfig so that the most recent valid accelerationRamp_mkradps2 is available to be reverted to later.
  const updateAccelerationRamp = (newAccelerationRamp: string) => {
    if (openLoopAccelerationIsValid(newAccelerationRamp)) {
      lastValidAccelerationRampRef.current = newAccelerationRamp;
    }
    editSensorsConfig("accelerationRamp_mkradps2", newAccelerationRamp);
  };

  const uiSensorModeLabels: Partial<Record<SensorMode, string>> = {
    [SensorMode.SENSORLESS]: "Sensorless HFI",
    [SensorMode.ENCODER]: "Encoder",
    [SensorMode.OPENLOOP]: "Open Loop Startup",
    [SensorMode.HALLSENSOR]: "Hall Sensor",
    [SensorMode.ABSOLUTE_ENCODER]: "Absolute Encoder",
  };
  let sensorModeOptions = Object.keys(SensorMode)
    .filter((k) => isFinite(Number(k)))
    .map((key, _) => {
      let mode: SensorMode = Number(key);
      return {
        value: mode,
        label: uiSensorModeLabels[mode] ?? SensorMode[mode],
      };
    });

  // Conditionally remove two of these assuming they aren't currently selected.
  if (!isSensorlessEnabled() && selectedSensorMode !== SensorMode.SENSORLESS) {
    sensorModeOptions = sensorModeOptions.filter(
      (opt) => opt.value !== SensorMode.SENSORLESS
    );
  }
  if (!isHallSensorSupported && selectedSensorMode !== SensorMode.HALLSENSOR) {
    sensorModeOptions = sensorModeOptions.filter(
      (opt) => opt.value !== SensorMode.HALLSENSOR
    );
  }

  // Sensorless HFI is disallowed for legal reasons.
  const showSensorlessNotice =
    isSensorlessEnabled() &&
    !isSensorlessEnabled(Env.Prod) &&
    selectedSensorMode === SensorMode.SENSORLESS;

  const setIsRunning = ( isRunning: boolean ) => {
    _setIsRunning( isRunning );
    setEnableNavigation( !isRunning );
  };

  // Calibration
  const calibrate = async () => {
    if (connectedDevice === undefined || !connectedDevice.initialized) {
      return;
    }

    setIsRunning(true);
    if ( selectedSensorMode !== SensorMode.ENCODER && selectedSensorMode !== SensorMode.HALLSENSOR && selectedSensorMode !== SensorMode.ABSOLUTE_ENCODER ) {
      throw new Error( `Sensor mode ${ SensorMode[ selectedSensorMode ] } is not implemented!` );
    }
    try {
      let results: IEncoderCalResultResponse | IHallSensorCalResultResponse | undefined;

      if ( selectedSensorMode === SensorMode.ENCODER || selectedSensorMode === SensorMode.ABSOLUTE_ENCODER ) {
        results = await runEncoderCalibration( connectedDevice.handle );
        if ( results.offset === EncoderCalibStatus.COMPLETE && (
            !isFinite(results.offset) || results.direction === EncoderDirection.UNKNOWN ) ) {
          setEncoderCalibStatus( `Encoder calibration results have invalid data. Please inform Salient Motion Support. Data: ${ JSON.stringify( results ) }` );
        } else {
          setEncoderCalibStatus( results.status );
          setNonEditableSensorsConfig({
            ...nonEditableSensorsConfig,
            encoderDirection: results.direction,
            encoderOffset: results.offset,
          });
        }
      } else {
        results = isHallSensorSupported
          ? await runHallSensorCalibration( connectedDevice.handle )
          : undefined;
        if ( results?.status === HallsensorCalibStatus.COMPLETE && ( !isFinite(results.forwardOffset) || !isFinite(results.reverseOffset) ||
            results.direction === HallsensorDirection.UNKNOWN ) ) {
          setHallSensorCalibStatus( `Hall sensor calibration results have invalid data. Please inform Salient Motion Support. Data: ${ JSON.stringify( results ) }` );
        } else if ( isDefined( results ) ) {
          setNonEditableSensorsConfig({
            ...nonEditableSensorsConfig,
            hallsensorDirection: results.direction,
            hallsensorForwardOffset: results.forwardOffset,
            hallsensorReverseOffset: results.reverseOffset,
          });
          setHallSensorCalibStatus( results.status );
        } else {
          setHallSensorCalibStatus( undefined );
        }
      }
      if ( results === undefined || ( results.status !== EncoderCalibStatus.COMPLETE && results.status !== HallsensorCalibStatus.COMPLETE ) ) {
        // Need to show something to user
        setShowCalibrationResultMessage(true);
      }
    } catch (e) {
      // ToDo: consider showing this to user
      console.warn("Exception from calibration:", e);
    } finally {
      setIsRunning(false);
    }
  };

  const encoderSelected = selectedSensorMode === SensorMode.ENCODER;
  const openLoopSelected = selectedSensorMode === SensorMode.OPENLOOP;
  const hallSensorSelected = selectedSensorMode === SensorMode.HALLSENSOR;
  const absoluteEncoderSelected = selectedSensorMode === SensorMode.ABSOLUTE_ENCODER;

  const offsetUnits = fullDisplayUnits[PacketId.ENCODER_CAL_RESULT]["offset"];
  const forwardOffsetUnits =
    fullDisplayUnits[PacketId.HALL_SENSOR_CAL_RESULT]["forwardOffset"];
  const reverseOffsetUnits =
    fullDisplayUnits[PacketId.HALL_SENSOR_CAL_RESULT]["reverseOffset"];
  const isTimeout = encoderCalibStatus === EncoderCalibStatus.FAILURE_TIMEOUT;
  const encoderStatusStr =
    encoderCalibStatus !== undefined
      ? encoderCalibrationStatusToString( encoderCalibStatus )
      : "---";
  const encoderOffsetStr =
    isFinite( nonEditableSensorsConfig.encoderOffset )
      ? nonEditableSensorsConfig.encoderOffset.toExponential(6)
      : undefined;
  const hallSensorStatusStr =
    hallSensorCalibStatus !== undefined
      ? hallSensorCalibrationStatusToString( hallSensorCalibStatus )
      : "---";
  const hallSensorForwardOffsetStr =
    isFinite( nonEditableSensorsConfig.hallsensorForwardOffset )
      ? nonEditableSensorsConfig.hallsensorForwardOffset.toExponential(6)
      : undefined;
  const hallSensorReverseOffsetStr =
    isFinite( nonEditableSensorsConfig.hallsensorReverseOffset )
      ? nonEditableSensorsConfig.hallsensorReverseOffset.toExponential(6)
      : undefined;
  const hallSensorDirectionStr = hallSensorDirectionToString( nonEditableSensorsConfig.hallsensorDirection ) ?? "---";

  let encoderDirectionStr = undefined;
  if ( nonEditableSensorsConfig.encoderDirection !== EncoderDirection.UNKNOWN ) {
    if ( nonEditableSensorsConfig.encoderDirection === EncoderDirection.COUNT_UP ) {
      encoderDirectionStr = "Forward";
    } else if ( nonEditableSensorsConfig.encoderDirection === EncoderDirection.COUNT_DOWN ) {
      encoderDirectionStr = "Reversed";
    }
  }

  const calibrateErrorsMemo = useMemo(
    () => calibrateErrors(connectedDevice, editableSensorsConfig, storedSensorsConfig),
    [connectedDevice, editableSensorsConfig, storedSensorsConfig]
  );
  const calibrateWarningsMemo: string[] = useMemo( () => {
    let warnings = [];
    if ( selectedSensorMode === SensorMode.ENCODER || selectedSensorMode === SensorMode.ABSOLUTE_ENCODER ) {
      const storedSensorMode = storedSensorsConfig.sensorMode;
      const isValidOffset = isFinite( nonEditableSensorsConfig.encoderOffset );
      const isValidDirection = ( nonEditableSensorsConfig.encoderDirection !== EncoderDirection.UNKNOWN );
      if ( !isValidOffset || !isValidDirection ) {
        if ( storedSensorMode === SensorMode.ENCODER || storedSensorMode === SensorMode.ABSOLUTE_ENCODER ) {
          warnings.push( "Before attempting to command motor, first calibrate, then Export and Update Config on device." );
        } else {
          warnings.push( "After Exporting and Updating Config on device, return here and Calibrate Encoder!" );
        }
      }
    }
    return warnings;
  }, [storedSensorsConfig, nonEditableSensorsConfig, selectedSensorMode]);
  const sensorErrorsMemo = useMemo(
    () => sensorErrors(connectedDevice, editableSensorsConfig),
    [connectedDevice, editableSensorsConfig]
  );
  const calibrationBlocked = calibrateErrorsMemo.length > 0;

  useEffect(() => {
    const warningsArray = calibrateWarningsMemo;
    const messagesArray = encoderSelected || hallSensorSelected || absoluteEncoderSelected
        ? [...calibrateErrorsMemo, ...warningsArray]
        : sensorErrorsMemo;
    if ( messagesArray.length === 0 ) {
      setWarningContents(null);
      return;
    }

    const sensorsWarning = (
      <>
        <H4>
          { (encoderSelected || absoluteEncoderSelected) ? ( messagesArray.length === warningsArray.length ? "Warning" : "Calibration Blocked" ) : "Unknown Error"}
        </H4>
        <ul style={{ color: Colors.WHITE }}>
          {messagesArray.map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      </>
    );
    setWarningContents(sensorsWarning);
  }, [
    setWarningContents,
    calibrationBlocked,
    calibrateErrorsMemo,
    calibrateWarningsMemo,
    sensorErrorsMemo,
    encoderSelected,
    hallSensorSelected,
    openLoopSelected,
    absoluteEncoderSelected,
  ]);

  return (
    <>
      <div style={configContainerStyle}>
        <div style={screenTitleStyle}>
          Sensors Configuration
        </div>
        <div style={inputContStyle}>
          <div style={{ flex: 1, display: "flex" }}>
            <FormGroup label="Sensor Mode">
              <HTMLSelect
                style={{
                  ...inputStyle,
                  width: FORM_WIDTH,
                }}
                fill={true}
                value={selectedSensorMode}
                options={sensorModeOptions}
                onChange={handleSensorModeChange}
              />
            </FormGroup>
          </div>

          {showSensorlessNotice && (
            <div style={{ color: Colors.YELLOW }}>
              <p>Note: Sensorless HFI mode is disabled in production.</p>
            </div>
          )}

          {/* Inputs if encoder or absolute encoder sensor mode is selected */}
          {(encoderSelected || absoluteEncoderSelected) && (
            <>
              <FormGroup
                label="Encoder CPR (counts per revolution)"
                labelInfo={<span style={{ color: Colors.RED }}>*</span>}
              >
                <NumericInput
                  fill={true}
                  value={editableSensorsConfig.encoderCpr}
                  onValueChange={(_, valStr) => updateEncoderCpr(valStr)}
                  stepSize={
                    1e-15 /* accept any floating point for now; integer validation happens later */
                  }
                  minorStepSize={null}
                  buttonPosition="none"
                  placeholder="Encoder CPR"
                  style={inputStyle}
                />
              </FormGroup>
              <FormGroup label="Encoder Status">
                <InputGroup
                  disabled={true}
                  value={encoderStatusStr}
                  placeholder="---"
                  style={{
                    backgroundColor: Colors.DARK_GREEN,
                    color: encoderCalibrationFailed( encoderCalibStatus )
                      ? Colors.RED
                      : Colors.WHITE,
                  }}
                />
              </FormGroup>
              <FormGroup label={`Encoder Offset (${offsetUnits})`}>
                <NumericInput
                  disabled={true}
                  fill={true}
                  value={encoderOffsetStr}
                  stepSize={1e-15 /* accept any floating point */}
                  minorStepSize={null}
                  buttonPosition="none"
                  placeholder="---"
                  style={inputStyle}
                />
              </FormGroup>
              <FormGroup label={`Encoder Direction`}>
                <NumericInput
                  disabled={true}
                  fill={true}
                  value={encoderDirectionStr}
                  buttonPosition="none"
                  placeholder="---"
                  style={inputStyle}
                />
              </FormGroup>
            </>
          )}

          {/* Hall sensor mode is selected */}
          {hallSensorSelected && (
            <>
              <FormGroup label="Hall Sensor Status">
                <InputGroup
                  disabled={true}
                  value={hallSensorStatusStr}
                  placeholder="---"
                  style={{
                    backgroundColor: Colors.DARK_GREEN,
                    color: hallSensorCalibrationFailed( hallSensorCalibStatus )
                      ? Colors.RED
                      : Colors.WHITE,
                  }}
                />
              </FormGroup>
              <FormGroup
                label={`Hall Sensor Forward Offset (${forwardOffsetUnits})`}
              >
                <NumericInput
                  disabled={true}
                  fill={true}
                  value={hallSensorForwardOffsetStr}
                  stepSize={1e-15 /* accept any floating point */}
                  minorStepSize={null}
                  buttonPosition="none"
                  placeholder="---"
                  style={inputStyle}
                />
              </FormGroup>
              <FormGroup
                label={`Hall Sensor Reverse Offset (${reverseOffsetUnits})`}
              >
                <NumericInput
                  disabled={true}
                  fill={true}
                  value={hallSensorReverseOffsetStr}
                  stepSize={1e-15 /* accept any floating point */}
                  minorStepSize={null}
                  buttonPosition="none"
                  placeholder="---"
                  style={inputStyle}
                />
              </FormGroup>
              <FormGroup label={`Hall Sensor Direction`}>
                <NumericInput
                  disabled={true}
                  fill={true}
                  value={hallSensorDirectionStr}
                  buttonPosition="none"
                  placeholder="---"
                  style={inputStyle}
                />
              </FormGroup>
            </>
          )}

          {/* Inputs if open loop sensor mode is selected */}
          {openLoopSelected && (
            <>
              <FormGroup
                label="Motor Open Loop Acceleration (mech. rad/s^2)"
                labelInfo={<span style={{ color: Colors.RED }}>*</span>}
              >
                {/* Motor open loop acceleration */}
                <NumericInput
                  fill={true}
                  value={editableSensorsConfig.accelerationRamp_mkradps2}
                  onValueChange={(_, valStr) => updateAccelerationRamp(valStr)}
                  stepSize={1e-15 /* accept any floating point */}
                  minorStepSize={null}
                  buttonPosition="none"
                  placeholder={DEFAULT_OPEN_LOOP_ACCEL_MKRADPS2.toString() /* ToDo: does this placeholder even get used? */}
                  style={inputStyle}
                />
              </FormGroup>
            </>
          )}
        </div>
        {/* Calibrate button if encoder sensor mode is selected */}
        {(encoderSelected || hallSensorSelected || absoluteEncoderSelected) && (
          <>
            <div
              style={{
                color: Colors.YELLOW,
                paddingLeft: 8,
                paddingRight: 8,
                display: "flex",
              }}
            >
              <span style={{ fontWeight: "bold" }}>Warning:&nbsp;</span> After
              clicking "Calibrate" below and confirming prompt, the connected
              motor will spin.
            </div>
            <div style={{ height: 16 }} />
            <div style={{ flex: 1, width: FORM_WIDTH }}>
              <Button
                backgroundColor={Colors.BUTTON_GREEN}
                textColor={Colors.WHITE}
                disabled={isRunning || calibrationBlocked}
                onClick={() => setShowWarningModal(true)}
              >
                Calibrate
              </Button>
            </div>
            <div style={{ height: 16 }} />
          </>
        )}
        <div style={{ display: "flex", width: FORM_WIDTH }}>
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.WHITE}
              textColor={Colors.BLACK}
              onClick={prev}
              disabled={prev === undefined}
            >
              Back
            </Button>
          </div>
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }} />
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.BUTTON_GREEN}
              textColor={Colors.WHITE}
              onClick={next}
              disabled={next === undefined}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
      <Dialog
        title={"Calibration " + (isTimeout ? "Timeout" : "Failure")}
        isOpen={showCalibrationResultMessage}
        onClose={() => setShowCalibrationResultMessage(false)}
        style={{ backgroundColor: Colors.LIGHT_GREEN }}
      >
        <div style={configModalStyle}>
          {(selectedSensorMode === SensorMode.ENCODER || selectedSensorMode === SensorMode.ABSOLUTE_ENCODER) ? (
            <p>
              A problem was detected during encoder calibration —{" "}
              { encoderCalibStatus && encoderCalibrationStatusToString( encoderCalibStatus ) }
              .
            </p>
          ) : selectedSensorMode === SensorMode.HALLSENSOR ? (
            <p>
              A problem was detected during hall sensor calibration —{" "}
              { hallSensorCalibStatus &&
                hallSensorCalibrationStatusToString( hallSensorCalibStatus ) }
              .
            </p>
          ) : "Unknown calibration error" }
        </div>
      </Dialog>
      <Dialog
        title="Warning"
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        style={{ backgroundColor: Colors.LIGHT_GREEN }}
      >
        <div style={configModalStyle}>
          <p>
            Motor will spin if you select Run Calibration, below!
            <br />
            <span style={{ color: Colors.YELLOW }}>
              <b>Motor must run unloaded!</b>
            </span>
          </p>
          <div style={{ alignSelf: "flex-end", display: "flex" }}>
            <Button
              backgroundColor={Colors.RED}
              textColor={Colors.BLACK}
              style={{ marginRight: 10 }}
              onClick={() => {
                setShowWarningModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              backgroundColor={Colors.WHITE}
              textColor={Colors.BLACK}
              onClick={() => {
                setShowWarningModal(false);
                calibrate();
              }}
            >
              Run Calibration
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export const sensorsAreValid = (
  editableSensorsConfig: Strings<IEditableSensorsConfig>,
): boolean => {
  const selectedSensorMode = parseInt( editableSensorsConfig.sensorMode, 10 ) as SensorMode;

  switch ( selectedSensorMode ) {
    case SensorMode.ENCODER:
      return true;
    case SensorMode.OPENLOOP:
      return openLoopAccelerationIsValid( editableSensorsConfig.accelerationRamp_mkradps2 );
    case SensorMode.SENSORLESS:
      return isSensorlessEnabled();
    case SensorMode.HALLSENSOR:
      return true;
    case SensorMode.ABSOLUTE_ENCODER:
      return encoderCprIsValid( editableSensorsConfig.encoderCpr );
    default:
      return false;
  }
}

const sensorErrors = (
  connectedDevice: Device | undefined,
  editableSensorsConfig: Strings<IEditableSensorsConfig>,
): string[] => {
  let errors = [];

  if ( connectedDevice === undefined || !connectedDevice.initialized ) {
    errors.push("A device must be connected.");
  }

  // Check for open loop acceleration ramp validity
  if ( !openLoopAccelerationIsValid( editableSensorsConfig.accelerationRamp_mkradps2 ) ) {
    errors.push(`Open Loop Acceleration must be a floating point value from 0 to ${MAX_OPEN_LOOP_ACCEL_MKRADPS2} inclusive.`);
  }

  return errors;
};

// Superset of sensorErrors()
const calibrateErrors = (
  connectedDevice: Device | undefined,
  editableSensorsConfig: Strings<IEditableSensorsConfig>,
  storedSensorsConfig: SensorsConfig,
): string[] => {
  let errors = [ ...sensorErrors( connectedDevice, editableSensorsConfig ) ];

  const selectedSensorMode = parseInt( editableSensorsConfig.sensorMode, 10 ) as SensorMode;

  const sensorModeNeedsUpdate = ( selectedSensorMode !== storedSensorsConfig.sensorMode );
  if ( sensorModeNeedsUpdate ) {
    errors.push("The changed sensor mode needs to be Exported and then applied to device with Update Config button");
  }

  // Check for encoder CPR validity
  if ( (selectedSensorMode === SensorMode.ENCODER || selectedSensorMode === SensorMode.ABSOLUTE_ENCODER) && !encoderCprIsValid( editableSensorsConfig.encoderCpr ) ) {
    errors.push("Encoder CPR must be an integer from 0 to 8192 inclusive.");
  }

  return errors;
};

// Input is mech. rad/s^2
const openLoopAccelerationIsValid = ( accelerationRampStr_mkradps2: string ): boolean => {
  let ramp = parseFloat(accelerationRampStr_mkradps2);
  return (ramp >= 0) && (ramp <= MAX_OPEN_LOOP_ACCEL_MKRADPS2);
}

const encoderCprIsValid = (encoderCprStr: string): boolean => {
  let encoderCpr = parseFloat(encoderCprStr);
  return Number.isInteger(encoderCpr) && ( 0 <= encoderCpr ) && ( encoderCpr <= 8192 );
}
